import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from "react";
import { SanityRawContent } from "../../model/common";
import { linkFragmentFromText, textFromBlock } from "../../utils/blocks";
import styles from "./BlockContentOutline.module.scss";
import classNames from "classnames";
import flatten from "lodash.flatten";
import { useWindowScroll } from "react-use";
import Link from "../Link/Link";
import useLocation from "../../utils/useLocation";
import Text, { TextVariant } from "../Text/Text";
import { prefersReducedMotion } from "../../utils/user-preferences";

// tslint:disable: no-increment-decrement

const HeadingAnchor = ({
  variant,
  heading,
  onClick
}: {
  variant: TextVariant;
  heading: SanityRawContent;
  highlighting?: "ancestorSelected" | "selected";
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}) => {
  const selector = `#${linkFragmentFromText(heading)}`;
  return (
    <Text variant={variant} gutterBottom={true}>
      <Link onClick={onClick} to={selector}>
        {textFromBlock(heading)}
      </Link>
    </Text>
  );
};

/**
 * We inline some top-level blocks that themselves contain rich text.
 * For now we go one level deep and only take top-level headings from
 * those blocks.
 */
const flattenNestedContent = (content: SanityRawContent): SanityRawContent =>
  flatten(
    content.map((block: SanityRawContent) => {
      // We'll probably need to make this rule more flexibile in future,
      // but we definitely don't always want to do it for e.g. collapsibleSections.
      if (block._type === "setOfMakes") {
        const firstHeading: Heading | undefined = findFirstNonH1Heading(
          block.content || []
        );
        if (firstHeading) {
          const firstHeadingElement = headingElement(firstHeading);
          return block.content.filter(
            (nestedBlock: SanityRawContent) =>
              nestedBlock.style === firstHeadingElement
          );
        }
      }
      return block;
    })
  );

export interface BlockContentOutlineProps {
  className?: string;
  content: SanityRawContent;
  subMenuStyle?: boolean;
  showOnMobile?: boolean;
}

// We care about four levels of heading but need to find
// the headings actually used, which generally start on
// h2 or h3.
type HeadingElement = "h1" | "h2" | "h3" | "h4";
type Heading = 1 | 2 | 3 | 4;
const headingElement = (h: Heading | undefined): HeadingElement =>
  `h${h}` as HeadingElement;
const headingNumber = (he: HeadingElement): Heading =>
  parseInt(he.substring(1), 10) as Heading;
const isHeadingElement = (h: string): h is HeadingElement =>
  /h([1234])/.test(h);
const nextLevel = (h: Heading): Heading | undefined => {
  if (h < 4) {
    return (h + 1) as Heading;
  }
  return undefined;
};

// If there is a H1 it'll be a single heading that we don't want as a root.
const findFirstNonH1Heading = (
  content: SanityRawContent
): Heading | undefined => {
  for (const block of content) {
    const { style } = block;
    if (style && isHeadingElement(style) && style !== "h1") {
      return headingNumber(style);
    }
  }
  return undefined;
};

interface RelevantHeadings {
  level1Block: HeadingElement | undefined;
  level2Block: HeadingElement | undefined;
  headings: SanityRawContent[];
}

const findRelevantHeadings = (
  content: SanityRawContent | undefined,
  alwaysUseLevel2: boolean
): RelevantHeadings => {
  const flattened = flattenNestedContent(content || []);
  const firstHeading: Heading | undefined = findFirstNonH1Heading(flattened);
  if (!firstHeading) {
    return {
      level1Block: undefined,
      level2Block: undefined,
      headings: []
    };
  }
  const level1Block: HeadingElement = headingElement(firstHeading);
  const level2: Heading | undefined = nextLevel(firstHeading);
  const level2Block: HeadingElement | undefined = level2
    ? headingElement(level2)
    : undefined;
  const level1Count = flattened.filter(
    (block: SanityRawContent) => block.style === level1Block
  ).length;
  const useLevel2 = alwaysUseLevel2 || level1Count <= 5;
  const headings: SanityRawContent[] = flattened.filter(
    (block: SanityRawContent) =>
      block.style &&
      (block.style === level1Block ||
        (useLevel2 && block.style === level2Block))
  );
  return { level1Block, level2Block, headings };
};

export const hasHeadings = (content: SanityRawContent | undefined): boolean =>
  findRelevantHeadings(content, false).headings.length > 0;

const BlockContentOutline = ({
  className,
  content,
  showOnMobile,
  subMenuStyle = false
}: BlockContentOutlineProps) => {
  const { level1Block, level2Block, headings } = findRelevantHeadings(
    content,
    !subMenuStyle
  );

  const { y: scrollPosition } = useWindowScroll();
  const [currentHeading, setCurrentHeading] = useState<string | undefined>(
    undefined
  );
  const { headingIds, reversedHeadingIds } = useMemo(() => {
    const headingIds: string[] = headings.map((h: SanityRawContent) =>
      linkFragmentFromText(h)
    );
    const reversedHeadingIds = [...headingIds].reverse();
    return { headingIds, reversedHeadingIds };
  }, [headings]);

  const location = useLocation();
  const ignoreScroll = useRef(0);
  const forceHeadingSelection = useCallback((id: string) => {
    ignoreScroll.current++;
    setCurrentHeading(id);
    setTimeout(() => ignoreScroll.current--, 200);
  }, []);
  const scrollWithForcedHeadingSelection = useCallback(
    (e: React.MouseEvent, id: string) => {
      if (prefersReducedMotion()) {
        forceHeadingSelection(id);
      } else {
        // Avoid querySelector as it doesn't accept all valid IDs.
        const target = document.getElementById(id);
        if (target) {
          e.preventDefault();

          ignoreScroll.current++;
          setTimeout(() => ignoreScroll.current--, 1000);
          target.scrollIntoView({
            behavior: "smooth"
          });
          const historyUrl = location.href.replace(/(#.+)?$/, `#${id}`);
          window.history.replaceState({}, "", historyUrl);
          setCurrentHeading(id);
        }
      }
    },
    [forceHeadingSelection, location.href]
  );
  useEffect(() => {
    if (ignoreScroll.current === 0) {
      const current = reversedHeadingIds
        .map(id => document.getElementById(id))
        .find(x => x && x.getBoundingClientRect().top <= 30);
      setCurrentHeading(current ? current.id : headingIds[0]);
    }
  }, [headingIds, reversedHeadingIds, scrollPosition]);
  useEffect(() => {
    if (location.hash) {
      forceHeadingSelection(location.hash.substring(1));
    }
  }, [forceHeadingSelection, location]);

  const isCurrentHeading = (heading: SanityRawContent) =>
    currentHeading === headingId(heading);

  const nested: {
    level1: SanityRawContent;
    level2s: SanityRawContent[];
  }[] = [];
  headings.forEach((heading: SanityRawContent) => {
    if (heading.style === level1Block) {
      nested.push({ level1: heading, level2s: [] });
    } else if (heading.style === level2Block) {
      const current = nested[nested.length - 1];
      if (current) {
        current.level2s.push(heading);
      }
    }
  });

  if (headings.length === 0) {
    // Avoid rendering an empty list that will take up margin space.
    return null;
  }
  return (
    <ul
      data-testid="outline"
      className={classNames(
        styles.root,
        className,
        showOnMobile && styles.showOnMobile,
        subMenuStyle && styles.subMenu
      )}
    >
      {nested.map(({ level1: h1, level2s: h2s }) => (
        <li
          key={h1._key}
          className={classNames(
            styles.listItem,
            isCurrentHeading(h1) ? styles.selected : undefined,
            h2s.some(isCurrentHeading) ? styles.descendentSelected : undefined
          )}
        >
          <HeadingAnchor
            heading={h1}
            variant={subMenuStyle ? "default" : "defaultBold"}
            onClick={e =>
              scrollWithForcedHeadingSelection(e, linkFragmentFromText(h1))
            }
          />
          {h2s.length > 0 && (
            <ul className={styles.secondaryList}>
              {h2s.map(h2 => (
                <li
                  key={h2._key}
                  className={classNames(
                    styles.listItem,
                    isCurrentHeading(h2) ? styles.selected : undefined
                  )}
                >
                  <HeadingAnchor
                    onClick={e =>
                      scrollWithForcedHeadingSelection(
                        e,
                        linkFragmentFromText(h2)
                      )
                    }
                    heading={h2}
                    variant="default"
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

const headingId = (heading: SanityRawContent) =>
  `#${linkFragmentFromText(heading)}`.substring(1);

export default BlockContentOutline;
